import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

/**
 * SlideChart Component
 * Displays interactive charts for BankReport data
 * 
 * Supported chart types:
 * - bar: Bar chart for comparisons
 * - pie: Pie chart for distributions
 * - line: Line chart for time series
 */
const SlideChart = ({ title, subtitle, chartType = 'bar', chartConfig }) => {
  const chartRef = useRef(null);

  // Default chart options
  const getChartOptions = () => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: true,
      animation: {
        duration: 1500,
        easing: 'easeInOutQuart'
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            color: '#6B7280',
            font: {
              size: 14,
              family: 'Inter, sans-serif'
            },
            padding: 20,
            usePointStyle: true,
            pointStyle: 'circle'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F3F4F6',
          bodyColor: '#E5E7EB',
          borderColor: '#6366F1',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y.toLocaleString('de-DE');
              } else if (context.parsed !== null) {
                label += context.parsed.toLocaleString('de-DE');
              }
              return label;
            }
          }
        }
      }
    };

    // Add scales for bar and line charts
    if (chartType === 'bar' || chartType === 'line') {
      baseOptions.scales = {
        x: {
          ticks: {
            color: '#9CA3AF',
            font: {
              size: 12
            }
          },
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
            drawBorder: false
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: '#9CA3AF',
            font: {
              size: 12
            },
            callback: function(value) {
              return value.toLocaleString('de-DE');
            }
          },
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
            drawBorder: false
          }
        }
      };
    }

    return baseOptions;
  };

  // Prepare chart data with Clara360 color scheme
  const getChartData = () => {
    if (!chartConfig || !chartConfig.data) {
      return {
        labels: ['Keine Daten'],
        datasets: [{
          data: [0],
          backgroundColor: ['#E5E7EB']
        }]
      };
    }

    const { labels, values, colors } = chartConfig.data;

    // Default Clara360 color palette
    const defaultColors = [
      '#6D28D9', // Purple
      '#A78BFA', // Light Purple
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F59E0B', // Amber
      '#EF4444', // Red
      '#8B5CF6', // Violet
      '#EC4899'  // Pink
    ];

    return {
      labels: labels || [],
      datasets: [{
        label: chartConfig.label || 'Wert',
        data: values || [],
        backgroundColor: colors || defaultColors,
        borderColor: chartType === 'line' ? (colors?.[0] || defaultColors[0]) : '#FFFFFF',
        borderWidth: chartType === 'pie' ? 2 : chartType === 'line' ? 3 : 0,
        tension: chartType === 'line' ? 0.4 : undefined,
        fill: chartType === 'line' ? false : undefined
      }]
    };
  };

  // Render appropriate chart type
  const renderChart = () => {
    const data = getChartData();
    const options = getChartOptions();

    switch (chartType) {
      case 'pie':
        return <Pie ref={chartRef} data={data} options={options} />;
      case 'line':
        return <Line ref={chartRef} data={data} options={options} />;
      case 'bar':
      default:
        return <Bar ref={chartRef} data={data} options={options} />;
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 p-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-1 h-8 bg-purple-600 rounded-full"></div>
          <h2 className="text-4xl font-bold text-gray-800 dark:text-white">
            {title || 'Datenvisualisierung'}
          </h2>
        </div>
        {subtitle && (
          <p className="text-gray-600 dark:text-gray-400 ml-7">
            {subtitle}
          </p>
        )}
      </motion.div>

      {/* Chart Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="flex-1 flex items-center justify-center p-8"
      >
        <div className="w-full max-w-4xl h-full">
          {renderChart()}
        </div>
      </motion.div>

      {/* Chart Type Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-500"
      >
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 rounded-full bg-purple-600"></div>
          <span>Interaktives Diagramm</span>
        </div>
        <span>•</span>
        <span className="capitalize">{chartType}</span>
      </motion.div>
    </div>
  );
};

export default SlideChart;

